import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SparklineProps {
    values: number[]
    width?: number
    height?: number
    baseline?: number | null
    tone?: 'up' | 'down' | 'flat' | 'auto'
    fill?: boolean
    className?: string
    ariaLabel?: string
}

const Sparkline = ({ values, width = 96, height = 28, baseline = null, tone = 'auto', fill = false, className, ariaLabel }: SparklineProps): ReactNode => {
    const clean = values.filter(value => Number.isFinite(value))

    if (clean.length < 2) {
        return <svg width={width} height={height} className={className} aria-hidden='true' />
    }

    let min = Math.min(...clean)
    let max = Math.max(...clean)

    if (baseline !== null && Number.isFinite(baseline)) {
        min = Math.min(min, baseline)
        max = Math.max(max, baseline)
    }

    if (min === max) {
        min -= 1
        max += 1
    }

    const pad = 2
    const stepX = (width - pad * 2) / (clean.length - 1)
    const y = (value: number): number => pad + (1 - (value - min) / (max - min)) * (height - pad * 2)
    const points = clean.map((value, index) => `${(pad + index * stepX).toFixed(1)},${y(value).toFixed(1)}`)
    const direction = tone === 'auto'
        ? clean[clean.length - 1] > (baseline ?? clean[0]) ? 'up' : clean[clean.length - 1] < (baseline ?? clean[0]) ? 'down' : 'flat'
        : tone
    const color = direction === 'up' ? 'var(--up)' : direction === 'down' ? 'var(--down)' : 'var(--flat)'
    const line = `M${points.join('L')}`
    const area = `${line}L${(pad + (clean.length - 1) * stepX).toFixed(1)},${height}L${pad},${height}Z`

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            className={cn('block shrink-0 overflow-visible', className)}
            role={ariaLabel ? 'img' : undefined}
            aria-label={ariaLabel}
            aria-hidden={ariaLabel ? undefined : true}
        >
            {baseline !== null && Number.isFinite(baseline) && (
                <line x1={0} x2={width} y1={y(baseline)} y2={y(baseline)} stroke='var(--muted-foreground)' strokeOpacity={0.45} strokeWidth={1} strokeDasharray='2 3' />
            )}

            {fill && <path d={area} fill={color} fillOpacity={0.1} />}

            <path d={line} fill='none' stroke={color} strokeWidth={1.5} strokeLinejoin='round' strokeLinecap='round' />
        </svg>
    )
}

export default Sparkline
